/**
 * Access Assignments — who carries which template, where.
 *
 * Every person has one company-wide default template, and optionally a
 * different template per project ("hats"): TC on job A, Estimator on job B,
 * PM on job C. Project hats always beat the default inside that project.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  makeApi, sectionCard, inputStyle, primaryBtn, dangerLink, errorBanner,
} from './AccessControlShared';
import { isExternalEmail } from '../../utils/menuAccess';

const isExternalTemplate = (t) => String(t?.key || '').startsWith('ext_');

const byJobNumber = (a, b) => String(a.job_number || '').localeCompare(
  String(b.job_number || ''), undefined, { numeric: true, sensitivity: 'base' }
);

/** Searchable project picker: number and name are two columns, sorted by number. */
const ProjectHatPicker = ({ projects, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const sorted = [...projects].sort(byJobNumber);
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(p =>
      String(p.job_number || '').toLowerCase().includes(q)
      || String(p.job_name || '').toLowerCase().includes(q)
    );
  }, [projects, query]);

  const selected = projects.find(p => String(p.id) === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const pick = (project) => {
    onChange(String(project.id));
    setQuery('');
    setOpen(false);
  };

  const col = { display: 'grid', gridTemplateColumns: '128px 1fr', gap: 8, alignItems: 'center' };

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 420, flex: '1 1 420px', maxWidth: 640 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ ...inputStyle, width: '100%', textAlign: 'left', cursor: 'pointer', ...col }}
      >
        {selected ? (
          <>
            <span style={{ fontWeight: 700, color: '#212529' }}>{selected.job_number}</span>
            <span style={{ color: '#495057', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.job_name}</span>
          </>
        ) : (
          <span style={{ color: '#868e96', gridColumn: '1 / -1' }}>Choose project</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 30, bottom: 'calc(100% + 4px)', left: 0, width: '100%', minWidth: 480,
          background: '#fff', border: '1px solid #ced4da', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid #e9ecef' }}>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search number or name"
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ ...col, padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#868e96', letterSpacing: 0.3 }}>
            <span>Number</span>
            <span>Name</span>
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', borderTop: '1px solid #f1f3f5' }}>
            {filtered.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => pick(p)}
                title={`${p.job_number}  ${p.job_name || ''}`}
                style={{
                  ...col, width: '100%', textAlign: 'left', padding: '8px 10px',
                  border: 'none', borderBottom: '1px solid #f8f9fa', cursor: 'pointer', fontSize: 13,
                  background: String(p.id) === String(value) ? '#e7f1ff' : '#fff',
                }}
              >
                <span style={{ fontWeight: 700, color: '#212529' }}>{p.job_number}</span>
                <span style={{ color: '#343a40' }}>{p.job_name}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 12, color: '#868e96', fontSize: 13 }}>No projects match.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const AccessAssignmentsView = ({ api, initialData = null }) => {
  const [users, setUsers] = useState(initialData?.users || []);
  const [templates, setTemplates] = useState(initialData?.templates || []);
  const [projects, setProjects] = useState(initialData?.projects || []);
  const [selectedUserId, setSelectedUserId] = useState(initialData?.selectedUserId ?? initialData?.users?.[0]?.id ?? null);
  const [assignments, setAssignments] = useState(initialData?.assignments || []);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [newProjectId, setNewProjectId] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, templatesRes, projectsRes] = await Promise.all([
        api('GET', '/access-control/users'),
        api('GET', '/access-control/templates'),
        api('GET', '/access-control/projects'),
      ]);
      setUsers(usersRes.data);
      setTemplates(templatesRes.data.filter(t => t.is_active));
      setProjects(projectsRes.data);
      setSelectedUserId(prev => prev || usersRes.data[0]?.id || null);
    } catch (e) {
      console.error(e);
      setError('Failed to load. ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadAssignments = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const res = await api('GET', `/access-control/assignments/${userId}`);
      setAssignments(res.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load assignments. ' + (e.message || ''));
    }
  }, [api]);

  useEffect(() => {
    if (!initialData) load();
  }, [initialData, load]);

  useEffect(() => {
    if (!initialData && selectedUserId) loadAssignments(selectedUserId);
  }, [initialData, selectedUserId, loadAssignments]);

  const selectedUser = users.find(u => u.id === selectedUserId) || null;
  const defaultAssignment = assignments.find(a => a.project_id === null) || null;
  const projectAssignments = assignments.filter(a => a.project_id !== null);

  // An outside e-mail gets External templates only, on a project only.
  // Staff never get an External template.
  const selectedIsExternal = isExternalEmail(selectedUser?.email);
  const templateChoices = useMemo(
    () => templates.filter(t => isExternalTemplate(t) === selectedIsExternal),
    [templates, selectedIsExternal]
  );

  const saveAssignment = async (projectId, templateId) => {
    try {
      setSaving(true);
      setError(null);
      await api('PUT', '/access-control/assignments', {
        userId: selectedUserId,
        projectId: projectId === null ? null : Number(projectId),
        templateId: Number(templateId),
      });
      await Promise.all([loadAssignments(selectedUserId), load()]);
    } catch (e) {
      console.error(e);
      setError('Failed to save assignment. ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (assignment) => {
    const label = assignment.project_id === null
      ? 'the company default template'
      : `the hat for job ${assignment.job_number}`;
    if (!window.confirm(`Remove ${label} from ${selectedUser?.display_name || 'this user'}?`)) return;
    try {
      setError(null);
      await api('DELETE', `/access-control/assignments/${assignment.id}`);
      await Promise.all([loadAssignments(selectedUserId), load()]);
    } catch (e) {
      console.error(e);
      setError('Failed to remove assignment. ' + (e.message || ''));
    }
  };

  const addProjectHat = async () => {
    if (!newProjectId || !newTemplateId) return;
    await saveAssignment(newProjectId, newTemplateId);
    setNewProjectId('');
    setNewTemplateId('');
  };

  const filteredUsers = users.filter(u => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.display_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const availableProjects = projects.filter(p => !projectAssignments.some(a => a.project_id === p.id));

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>Loading assignments…</div>;
  }

  return (
    <div>
      {errorBanner(error)}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* User list */}
        <div style={{ ...sectionCard, width: 320, flexShrink: 0 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef' }}>
            <input
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              placeholder="Search people…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            {filteredUsers.map(u => (
              <button
                key={u.id}
                onClick={() => setSelectedUserId(u.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                  border: 'none', borderBottom: '1px solid #f1f3f5', cursor: 'pointer',
                  backgroundColor: u.id === selectedUserId ? '#e7f1ff' : '#fff',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{u.display_name || u.email}</div>
                <div style={{ fontSize: 11, color: '#868e96' }}>
                  {u.default_template_name || 'no default template'}
                  {u.project_assignment_count > 0 && ` · ${u.project_assignment_count} project hat${u.project_assignment_count === 1 ? '' : 's'}`}
                  {u.exception_count > 0 && ` · ${u.exception_count} exception${u.exception_count === 1 ? '' : 's'}`}
                </div>
              </button>
            ))}
            {filteredUsers.length === 0 && (
              <div style={{ padding: 20, fontSize: 13, color: '#868e96' }}>No users match.</div>
            )}
          </div>
        </div>

        {/* Selected user's hats */}
        <div style={{ ...sectionCard, flex: 1, minWidth: 520 }}>
          {!selectedUser ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>Select a person.</div>
          ) : (
            <>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #e9ecef' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>{selectedUser.display_name || selectedUser.email}</div>
                <div style={{ fontSize: 12, color: '#868e96' }}>{selectedUser.email}</div>
              </div>

              {/* Company default */}
              <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f3f5' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#495057', marginBottom: 6 }}>
                  Company default template
                </div>
                {selectedIsExternal ? (
                  <div style={{
                    fontSize: 12.5, color: '#92400e', backgroundColor: '#fff7ed',
                    border: '1px solid #fed7aa', borderRadius: 6, padding: '8px 10px',
                  }}>
                    External user. No company-wide access: give them an External template on each job below.
                    {defaultAssignment && (
                      <>
                        {' '}A company default is still on file and is ignored.
                        <button style={dangerLink} onClick={() => removeAssignment(defaultAssignment)}>remove it</button>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: '#868e96', marginBottom: 10 }}>
                      Applies everywhere, unless a project hat below overrides it.
                    </div>
                    <select
                      style={{ ...inputStyle, minWidth: 280 }}
                      disabled={saving}
                      value={defaultAssignment?.template_id || ''}
                      onChange={e => e.target.value && saveAssignment(null, e.target.value)}
                    >
                      <option value="">— no default (no access) —</option>
                      {templateChoices.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {defaultAssignment && (
                      <button style={dangerLink} onClick={() => removeAssignment(defaultAssignment)}>remove</button>
                    )}
                  </>
                )}
              </div>

              {/* Project hats */}
              <div style={{ padding: '16px 18px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#495057', marginBottom: 6 }}>
                  Project hats <span style={{ fontWeight: 400, color: '#868e96' }}>(matrix organization — a different template per project)</span>
                </div>
                {projectAssignments.length === 0 && (
                  <div style={{ fontSize: 12.5, color: '#868e96', margin: '8px 0 12px' }}>
                    {selectedIsExternal
                      ? 'None yet — this person cannot open any job until a hat is added.'
                      : 'None yet — this person uses the company default on every project.'}
                  </div>
                )}
                {projectAssignments.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '8px 0 12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e9ecef', color: '#495057' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Project</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Template on this project</th>
                        <th style={{ padding: '6px 8px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {projectAssignments.map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                          <td style={{ padding: '8px 8px', fontWeight: 600 }}>
                            {a.job_number} <span style={{ color: '#868e96', fontWeight: 400 }}>{a.job_name}</span>
                          </td>
                          <td style={{ padding: '8px 8px' }}>
                            <select
                              style={{ ...inputStyle, minWidth: 220 }}
                              disabled={saving}
                              value={a.template_id}
                              onChange={e => saveAssignment(a.project_id, e.target.value)}
                            >
                              {templateChoices.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                              {!templateChoices.some(t => t.id === a.template_id) && (
                                <option value={a.template_id}>{a.template_name || 'current template'} (does not fit this user)</option>
                              )}
                            </select>
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                            <button style={dangerLink} onClick={() => removeAssignment(a)}>remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{
                  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                  padding: '12px 14px', backgroundColor: '#f8f9fa', borderRadius: 8,
                }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#495057' }}>Add hat:</span>
                  <ProjectHatPicker
                    projects={availableProjects}
                    value={newProjectId}
                    onChange={setNewProjectId}
                  />
                  <select style={{ ...inputStyle, minWidth: 200 }} value={newTemplateId} onChange={e => setNewTemplateId(e.target.value)}>
                    <option value="">{selectedIsExternal ? 'Choose External template…' : 'Choose template…'}</option>
                    {templateChoices.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <button
                    style={primaryBtn(!newProjectId || !newTemplateId || saving)}
                    disabled={!newProjectId || !newTemplateId || saving}
                    onClick={addProjectHat}
                  >
                    Add
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AccessAssignmentsTab = () => {
  const { instance, accounts } = useMsal();
  const api = useMemo(() => makeApi(instance, accounts), [instance, accounts]);
  return <AccessAssignmentsView api={api} />;
};

export default AccessAssignmentsTab;
